import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatchData } from "./dispatch/useDispatchData";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../lib/firebase";

function normalize(value) {
  return String(value || "").trim().toLowerCase();
}

function toDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  if (value?.seconds) return new Date(value.seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatResponseTime(seconds) {
  if (seconds == null || Number.isNaN(seconds)) return "—";
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins <= 0) return `${secs} sec`;
  return `${mins} min ${secs} sec`;
}

function avg(values) {
  const clean = values.filter((v) => typeof v === "number" && !Number.isNaN(v));
  if (!clean.length) return null;
  return clean.reduce((a, b) => a + b, 0) / clean.length;
}

function getCallDate(call) {
  return (
    toDate(call.createdAt) ||
    toDate(call.dispatchedAt) ||
    toDate(call.queuedAt) ||
    toDate(call.updatedAt)
  );
}

function getMondayStart(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  return x;
}

function formatWeekLabel(start) {
  const end = new Date(start);
  end.setDate(start.getDate() + 6);

  return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
}

function isInCurrentMondayToSundayWeek(date) {
  if (!date) return false;

  const now = new Date();

  const start = new Date(now);
  start.setHours(0, 0, 0, 0);

  const day = start.getDay();
  const diff = day === 0 ? -6 : 1 - day;

  start.setDate(start.getDate() + diff);

  const end = new Date(start);
  end.setDate(start.getDate() + 7);

  return date >= start && date < end;
}

function getResponseMinutes(call) {
  const dispatchTime = toDate(call.dispatchedAt);
  if (!dispatchTime) return null;

  const firstUnitOnScene = Object.values(call.unitTimelines || {})
    .map((timeline) => toDate(timeline?.onSceneAt))
    .filter(Boolean)
    .sort((a, b) => a - b)[0];

  const callLevelOnScene = toDate(call.onSceneAt);

  const earliestOnScene =
    firstUnitOnScene && callLevelOnScene
      ? firstUnitOnScene < callLevelOnScene
        ? firstUnitOnScene
        : callLevelOnScene
      : firstUnitOnScene || callLevelOnScene;

  if (!earliestOnScene) return null;

  const seconds = (earliestOnScene - dispatchTime) / 1000;
  return seconds >= 0 ? seconds : null;
}

function getCourtesyResponseMinutes(call) {
  const dispatchTime =
    toDate(call.dispatchedAt) ||
    toDate(call.hospitalTransport?.dispatchedAt) ||
    toDate(call.hq?.courtesyDispatchedAt);

  if (!dispatchTime) return null;

  const firstUnitOnScene = Object.values(call.unitTimelines || {})
    .map((timeline) => toDate(timeline?.onSceneAt))
    .filter(Boolean)
    .sort((a, b) => a - b)[0];

  const callLevelOnScene = toDate(call.onSceneAt);
  const earliestOnScene = firstUnitOnScene || callLevelOnScene;

  if (!earliestOnScene) return null;

  const seconds = (earliestOnScene - dispatchTime) / 1000;
  return seconds >= 0 ? seconds : null;
}

function isCourtesyCall(call) {
  const status = normalize(call.status);

  const isCancelled =
    status === "cancelled" ||
    status === "canceled" ||
    call.hq?.courtesyCancelled === true ||
    normalize(call.hq?.courtesyStatus) === "cancelled" ||
    normalize(call.hospitalTransport?.status) === "cancelled";

  if (isCancelled) return false;

  return call.callType === "courtesyTransport" || !!call.hq?.courtesyRequested;
}

function isEmergencyCall(call) {
  return call.callType === "emergency";
}

function isStandbyCall(call) {
  return call.callType === "standby";
}

function isAmbulanceTransportCall(call) {
  const ht = call?.hospitalTransport || {};
  const status = normalize(call.status);
  const transportStatus = normalize(ht.status);

  const isCancelled =
    status === "cancelled" ||
    status === "canceled" ||
    transportStatus === "cancelled" ||
    transportStatus === "canceled" ||
    ht.cancelled === true ||
    ht.canceled === true;

  if (isCancelled) return false;

  return (
    ht.requested === true ||
    !!ht.ambulanceUnitId ||
    !!call.ambulanceUnitId ||
    normalize(call.callType) === "ambulance" ||
    normalize(call.callType) === "ambulancetransport" ||
    normalize(call.transportType) === "ambulance"
  );
}

function getAmbulanceTransportDate(call) {
  return (
    toDate(call.hospitalTransport?.arrivedAt) ||
    toDate(call.hospitalTransport?.transportingAt) ||
    toDate(call.hospitalTransport?.requestedAt) ||
    toDate(call.hospitalTransport?.createdAt) ||
    toDate(call.arrivedAt) ||
    toDate(call.transportingAt) ||
    getCallDate(call)
  );
}

function isTransportUnitName(name = "") {
  const n = normalize(name);
  return (
    n.includes("transport") ||
    n.includes("ambulance") ||
    n.includes("trinity") ||
    n.includes("cromwell")
  );
}

function getPatientBed(patient) {
  return String(patient.currentBed || patient.bed || patient.hqBed || "").trim();
}

function isPatientInWaitingRoom(patient) {
  return (
    patient.waitingRoom === true ||
    normalize(patient.location) === "waiting room" ||
    normalize(patient.currentLocation) === "waiting room" ||
    normalize(patient.currentLocation) === "waitingroom"
  );
}

function isCurrentPatientAtHQ(patient) {
  const trackerStatus = normalize(patient.trackerStatus);

  if (trackerStatus !== "active") return false;

  if (
    patient.discharged === true ||
    patient.closed === true ||
    patient.encounterClosed === true ||
    patient.currentEncounterClosed === true
  ) {
    return false;
  }

  const bed = getPatientBed(patient);
  const waiting = isPatientInWaitingRoom(patient);

  return Boolean(bed) || waiting;
}

function isCallClosed(call, units) {
  const callStatus = normalize(call.status);

  if (
    [
      "cleared",
      "clear",
      "closed",
      "cancelled",
      "canceled",
      "resolved",
      "completed",
    ].includes(callStatus)
  ) {
    return true;
  }

  if (call.hq?.boardClosed === true) return true;

  const assignedIds = Array.isArray(call.assignedUnitIds)
    ? call.assignedUnitIds
    : [];

  const statusIds = Object.keys(call.unitStatuses || {});

  if (!assignedIds.length && !statusIds.length) {
    return true;
  }

  const allStatuses = [...new Set([...assignedIds, ...statusIds])]
    .map((id) =>
      normalize(call.unitStatuses?.[id] || units.find((u) => u.id === id)?.status)
    )
    .filter(Boolean);

  if (!allStatuses.length) return true;

  return allStatuses.every((status) =>
    ["clear", "cleared", "available", "avail"].includes(status)
  );
}

function BarRow({ label, value, max }) {
  const width = max > 0 ? Math.max(4, (value / max) * 100) : 0;

  return (
    <div style={styles.barRow}>
      <div style={styles.barLabel}>{label}</div>
      <div style={styles.barTrack}>
        <div style={{ ...styles.barFill, width: `${width}%` }} />
      </div>
      <div style={styles.barValue}>{value}</div>
    </div>
  );
}

export default function StatusBoard({ compact = false }) {
  const { calls = [], units = [] } = useDispatchData({});
  const [manualPatients, setManualPatients] = useState([]);
  const [selectedWeekStart, setSelectedWeekStart] = useState(() =>
  getMondayStart(new Date())
);
  const navigate = useNavigate();

  const fieldAccessSource = sessionStorage.getItem("fieldAccessSource");
  const isDispatchMode = fieldAccessSource === "dispatch";
  const isCommandMode = fieldAccessSource === "command";

  const isSmallScreen =
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 768px)").matches;

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "patients"), (snap) => {
      const items = snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      setManualPatients(items);
    });

    return () => unsub();
  }, []);

  const manualStatusCalls = useMemo(() => {
    const dispatchPatientIds = new Set(
      calls.map((call) => String(call.patientId || "")).filter(Boolean)
    );

    return manualPatients
      .filter((patient) => !dispatchPatientIds.has(String(patient.id)))
      .filter(isCurrentPatientAtHQ)
      .map((patient) => {
        const bed = getPatientBed(patient);
        const waiting = isPatientInWaitingRoom(patient);

        const complaint =
          patient.currentChiefComplaint ||
          patient.chiefComplaint ||
          patient.complaint ||
          "Walk-up Patient";

        return {
          id: `manual-${patient.id}`,
          patientId: patient.id,
          encounterId: patient.currentEncounterId || "",
          callType: "emergency",
          manualStatusOnly: true,
          trackerStatus: patient.trackerStatus || "",
          chiefComplaint: complaint,
          currentChiefComplaint: complaint,
          nature: complaint,
          status: bed ? "In Bed" : "Waiting Room",
          location: bed ? `Bed ${bed}` : waiting ? "Waiting Room" : "",
          patientAtHQ: Boolean(bed) || waiting,
          hq: {
            bedNumber: bed,
            firstName: patient.fullName || patient.firstName || "",
          },
          createdAt: patient.createdAt || patient.updatedAt,
          updatedAt: patient.updatedAt || patient.createdAt,
        };
      });
  }, [manualPatients, calls]);

  const allStatusCalls = useMemo(() => {
    return [...calls, ...manualStatusCalls];
  }, [calls, manualStatusCalls]);

  const activeCalls = useMemo(
    () => allStatusCalls.filter((call) => !isCallClosed(call, units)),
    [allStatusCalls, units]
  );

  const analytics = useMemo(() => {
    const weekStart = getMondayStart(selectedWeekStart);
const weekEnd = new Date(weekStart);
weekEnd.setDate(weekStart.getDate() + 7);

const thisWeekCalls = calls.filter((call) => {
  const date = getCallDate(call);
  return date && date >= weekStart && date < weekEnd;
});

const emergencyCalls = thisWeekCalls.filter(isEmergencyCall);
const courtesyCalls = thisWeekCalls.filter(isCourtesyCall);
const standbyCalls = thisWeekCalls.filter(isStandbyCall);
const ambulanceTransportCalls = thisWeekCalls.filter(isAmbulanceTransportCall);

    const emergencyResponseTimes = emergencyCalls
      .map(getResponseMinutes)
      .filter((v) => v != null);

    const courtesyResponseTimes = courtesyCalls
      .map(getCourtesyResponseMinutes)
      .filter((v) => v != null);

    const callsByDay = {};
    const callsByHour = {};
    const callsByUnit = {};
    const callTypes = {};
    const ambulanceTransportsByDay = {};

    emergencyCalls.forEach((call) => {
      const date = getCallDate(call);

      if (date) {
        const day = date.toLocaleDateString(undefined, { weekday: "short" });
        const hour = date.getHours();
        const hourLabel =
          hour === 0
            ? "12a"
            : hour < 12
            ? `${hour}a`
            : hour === 12
            ? "12p"
            : `${hour - 12}p`;

        callsByDay[day] = (callsByDay[day] || 0) + 1;
        callsByHour[hourLabel] = (callsByHour[hourLabel] || 0) + 1;
      }

      const type = String(
        call.currentChiefComplaint ||
          call.chiefComplaint ||
          call.nature ||
          "Unknown"
      ).trim();

      if (type) {
        callTypes[type] = (callTypes[type] || 0) + 1;
      }

      const unitIdsForStats = Array.from(
        new Set([
          ...(call.assignedUnitIds || []),
          ...Object.keys(call.unitTimelines || {}),
          ...(call.clearedUnitIds || []),
        ])
      );

      unitIdsForStats.forEach((id) => {
        const unit = units.find((u) => u.id === id);
        const name = unit?.name || id;

        if (isTransportUnitName(name)) return;

        callsByUnit[name] = (callsByUnit[name] || 0) + 1;
      });
    });

    ambulanceTransportCalls.forEach((call) => {
      const date = getAmbulanceTransportDate(call);
      if (!date) return;

      const day = date.toLocaleDateString(undefined, { weekday: "short" });
      ambulanceTransportsByDay[day] = (ambulanceTransportsByDay[day] || 0) + 1;
    });

   const dayOrder = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    return {
      totalEmergencies: emergencyCalls.length,
      totalCourtesy: courtesyCalls.length,
      totalStandby: standbyCalls.length,
      totalAmbulanceTransports: ambulanceTransportCalls.length,

      totalPatientEncounters: manualPatients.length,

      currentPatientsAtHQ: manualPatients.filter(isCurrentPatientAtHQ).length,

      avgEmergencyResponse: avg(emergencyResponseTimes),
      avgCourtesyResponse: avg(courtesyResponseTimes),

      callsByDay: dayOrder.map((day) => ({
        label: day,
        value: callsByDay[day] || 0,
      })),

      ambulanceTransportsByDay: dayOrder.map((day) => ({
        label: day,
        value: ambulanceTransportsByDay[day] || 0,
      })),

      callsByHour: Object.entries(callsByHour)
        .map(([label, value]) => ({ label, value }))
        .sort((a, b) => {
          const parse = (label) => {
            if (label === "12a") return 0;
            if (label === "12p") return 12;
            if (label.endsWith("a")) return parseInt(label, 10);
            return parseInt(label, 10) + 12;
          };
          return parse(a.label) - parse(b.label);
        }),

      callsByUnit: Object.entries(callsByUnit)
        .map(([label, value]) => ({ label, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 8),

      topCallTypes: Object.entries(callTypes)
        .map(([label, value]) => ({ label, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 5),
    };
  }, [calls, manualPatients, units, selectedWeekStart]);

  const maxDay = Math.max(...analytics.callsByDay.map((x) => x.value), 1);
  const maxHour = Math.max(...analytics.callsByHour.map((x) => x.value), 1);
  const maxUnit = Math.max(...analytics.callsByUnit.map((x) => x.value), 1);
  const maxType = Math.max(...analytics.topCallTypes.map((x) => x.value), 1);
  const maxAmbulanceTransportDay = Math.max(
    ...analytics.ambulanceTransportsByDay.map((x) => x.value),
    1
  );

  return (
    <div
      style={{
        ...styles.page,
        ...(isSmallScreen ? styles.pageMobile : {}),
        height: compact ? "100%" : "calc(100vh - 40px)",
      }}
    >
      {!compact && (
        <div style={styles.floatingNav}>
          {isDispatchMode ? (
            <>
              <button
                style={styles.navBtn}
                onClick={() => navigate("/field-access/dispatch")}
              >
                Dispatch
              </button>

              <button style={styles.navBtnActive}>Status Board</button>
            </>
          ) : isCommandMode ? (
            <>
              <button
                style={styles.navBtn}
                onClick={() => navigate("/field-access/command")}
              >
                Command
              </button>

              <button
                style={styles.navBtn}
                onClick={() => navigate("/field-access/hq")}
              >
                PT Tracker
              </button>

              <button
                style={styles.navBtn}
                onClick={() =>
                  navigate("/field-access/hq", {
                    state: { activeTab: "patients" },
                  })
                }
              >
                Patients
              </button>

              <button
                style={styles.navBtn}
                onClick={() => navigate("/field-access/dispatch")}
              >
                Dispatch
              </button>

              <button style={styles.navBtnActive}>Status Board</button>
            </>
          ) : (
            <>
              <button
                style={styles.navBtn}
                onClick={() => navigate("/field-access/hq")}
              >
                PT Tracker
              </button>

              <button
                style={styles.navBtn}
                onClick={() =>
                  navigate("/field-access/hq", {
                    state: { activeTab: "patients" },
                  })
                }
              >
                Patients
              </button>

              <button style={styles.navBtnActive}>Status Board</button>
            </>
          )}
        </div>
      )}

      <div style={{ ...styles.left, ...(isSmallScreen ? styles.panelMobile : {}) }}>
        <div style={styles.header}>Active Calls</div>

        <div style={styles.grid}>
          {!activeCalls.length && <div style={styles.empty}>No Active Calls</div>}

          {activeCalls.map((call) => {
            const callUnits = (call.assignedUnitIds || [])
              .map((id) => units.find((u) => u.id === id))
              .filter(Boolean);

            return (
              <div key={call.id} style={styles.card}>
                <div style={styles.title}>
                  {call.location || "Unknown Location"} • {call.nature || "—"}
                </div>

                <div style={styles.status}>
                  {(call.status || "Active").toUpperCase()}
                </div>

                <div style={styles.units}>
                  {callUnits.length === 0 && (
                    <div style={styles.unit}>No Units Assigned</div>
                  )}

                  {callUnits.map((u) => {
                    const unitStatus = call.unitStatuses?.[u.id] || u.status || "—";

                    return (
                      <div key={u.id} style={styles.unit}>
                        {u.name} • {unitStatus}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ ...styles.right, ...(isSmallScreen ? styles.panelMobile : {}) }}>
        <div style={styles.header}>Status Board Analytics</div>
        <div style={styles.weekControls}>
  <button
    style={styles.weekBtn}
    onClick={() =>
      setSelectedWeekStart((prev) => {
        const next = new Date(prev);
        next.setDate(next.getDate() - 7);
        return getMondayStart(next);
      })
    }
  >
    ← Previous Week
  </button>

  <div style={styles.weekLabel}>
    {formatWeekLabel(selectedWeekStart)}
  </div>

  <button
    style={styles.weekBtn}
    onClick={() =>
      setSelectedWeekStart((prev) => {
        const next = new Date(prev);
        next.setDate(next.getDate() + 7);
        return getMondayStart(next);
      })
    }
  >
    Next Week →
  </button>
</div>

        <div style={styles.analytics}>
          <div style={{ ...styles.statGrid, ...(isSmallScreen ? styles.statGridMobile : {}) }}>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Emergencies</div>
              <div style={styles.statValue}>{analytics.totalEmergencies}</div>
            </div>

            <div style={styles.statCard}>
              <div style={styles.statLabel}>Courtesy Transports</div>
              <div style={styles.statValue}>{analytics.totalCourtesy}</div>
            </div>

            <div style={styles.statCard}>
              <div style={styles.statLabel}>Ambulance Transports</div>
              <div style={styles.statValue}>
                {analytics.totalAmbulanceTransports}
              </div>
            </div>

            <div style={styles.statCard}>
              <div style={styles.statLabel}>Patient Encounters</div>
              <div style={styles.statValue}>{analytics.totalPatientEncounters}</div>
            </div>

            <div style={styles.statCard}>
              <div style={styles.statLabel}>Current Patients at HQ</div>
              <div style={styles.statValue}>{analytics.currentPatientsAtHQ}</div>
            </div>

            <div style={styles.statCard}>
              <div style={styles.statLabel}>Avg Emergency Response</div>
              <div style={styles.statValue}>
                {formatResponseTime(analytics.avgEmergencyResponse)}
              </div>
            </div>
          </div>

          <div style={{ ...styles.chartGrid, ...(isSmallScreen ? styles.chartGridMobile : {}) }}>
            <div style={styles.chartCard}>
              <div style={styles.chartTitle}>Emergency Calls by Day</div>
              {analytics.callsByDay.map((row) => (
                <BarRow key={row.label} label={row.label} value={row.value} max={maxDay} />
              ))}
            </div>

            <div style={styles.chartCard}>
              <div style={styles.chartTitle}>Ambulance Transports by Day</div>
              {analytics.ambulanceTransportsByDay.map((row) => (
                <BarRow
                  key={row.label}
                  label={row.label}
                  value={row.value}
                  max={maxAmbulanceTransportDay}
                />
              ))}
            </div>

            <div style={styles.chartCard}>
              <div style={styles.chartTitle}>Emergency Calls by Time</div>
              {analytics.callsByHour.length === 0 ? (
                <div style={styles.emptySmall}>No call time data yet</div>
              ) : (
                analytics.callsByHour.map((row) => (
                  <BarRow key={row.label} label={row.label} value={row.value} max={maxHour} />
                ))
              )}
            </div>

            <div style={styles.chartCard}>
              <div style={styles.chartTitle}>Emergency Calls by Unit</div>
              {analytics.callsByUnit.length === 0 ? (
                <div style={styles.emptySmall}>No unit data yet</div>
              ) : (
                analytics.callsByUnit.map((row) => (
                  <BarRow key={row.label} label={row.label} value={row.value} max={maxUnit} />
                ))
              )}
            </div>

            <div style={styles.chartCard}>
              <div style={styles.chartTitle}>Top 5 Emergency Call Types</div>
              {analytics.topCallTypes.length === 0 ? (
                <div style={styles.emptySmall}>No call type data yet</div>
              ) : (
                analytics.topCallTypes.map((row) => (
                  <BarRow key={row.label} label={row.label} value={row.value} max={maxType} />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    display: "grid",
    gridTemplateColumns: "300px minmax(0, 1fr)",
    gap: 8,
    background: "#e5e7eb",
    position: "relative",
    overflow: "hidden",
  },
  left: {
    display: "flex",
    flexDirection: "column",
    background: "#0f172a",
    borderRadius: 14,
    overflow: "hidden",
  },
  right: {
    display: "flex",
    flexDirection: "column",
    background: "#020617",
    borderRadius: 14,
    overflow: "hidden",
  },
  header: {
    padding: "10px 14px",
    fontWeight: 900,
    fontSize: 15,
    color: "#fff",
    background: "#020617",
    borderBottom: "1px solid #1e293b",
  },
  grid: {
    padding: 8,
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 8,
    overflowY: "auto",
    alignContent: "start",
  },
  card: {
    background: "#111827",
    border: "1px solid #1f2937",
    borderRadius: 12,
    padding: 10,
    display: "flex",
    flexDirection: "column",
    gap: 7,
  },
  title: {
    fontSize: 12,
    fontWeight: 900,
    color: "#f8fafc",
    lineHeight: 1.3,
  },
  status: {
    fontSize: 10,
    fontWeight: 900,
    color: "#38bdf8",
    letterSpacing: 0.5,
  },
  units: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
  },
  unit: {
    fontSize: 10,
    color: "#cbd5e1",
  },
  weekControls: {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  padding: "8px 10px",
  background: "#020617",
  borderBottom: "1px solid #1e293b",
},

weekBtn: {
  padding: "6px 10px",
  background: "#1e293b",
  color: "#fff",
  border: "1px solid #334155",
  borderRadius: 8,
  fontSize: 11,
  fontWeight: 900,
  cursor: "pointer",
},

weekLabel: {
  color: "#f8fafc",
  fontSize: 12,
  fontWeight: 900,
  minWidth: 180,
  textAlign: "center",
},
  analytics: {
    padding: 10,
    display: "grid",
    gridTemplateRows: "auto minmax(0, 1fr)",
    gap: 10,
    overflow: "auto",
  },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
    gap: 8,
  },
  statCard: {
    background: "linear-gradient(180deg, #111827, #0f172a)",
    border: "1px solid #1e293b",
    borderRadius: 14,
    padding: 10,
    minHeight: 70,
  },
  statLabel: {
    fontSize: 9,
    color: "#94a3b8",
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    lineHeight: 1.15,
  },
  statValue: {
    marginTop: 7,
    fontSize: 22,
    color: "#f8fafc",
    fontWeight: 950,
    lineHeight: 1,
  },
  chartGrid: {
    minHeight: 0,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
    overflow: "visible",
  },
  chartCard: {
    background: "#0f172a",
    border: "1px solid #1e293b",
    borderRadius: 14,
    padding: 10,
    overflow: "hidden",
  },
  chartTitle: {
    color: "#f8fafc",
    fontSize: 13,
    fontWeight: 900,
    marginBottom: 8,
  },
  barRow: {
    display: "grid",
    gridTemplateColumns: "72px 1fr 28px",
    alignItems: "center",
    gap: 7,
    marginBottom: 6,
  },
  barLabel: {
    color: "#cbd5e1",
    fontSize: 10,
    fontWeight: 900,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  barTrack: {
    height: 9,
    background: "#1e293b",
    borderRadius: 999,
    overflow: "hidden",
  },
  barFill: {
    height: "100%",
    background: "linear-gradient(90deg, #38bdf8, #22c55e)",
    borderRadius: 999,
  },
  barValue: {
    color: "#f8fafc",
    fontSize: 10,
    fontWeight: 900,
    textAlign: "right",
  },
  empty: {
    color: "#94a3b8",
    fontSize: 12,
    padding: 10,
  },
  emptySmall: {
    color: "#94a3b8",
    fontSize: 11,
    padding: "4px 0",
  },
  floatingNav: {
    position: "absolute",
    top: 8,
    right: 12,
    zIndex: 9999,
    display: "flex",
    gap: 6,
  },
  navBtn: {
    padding: "5px 10px",
    background: "#1e293b",
    color: "#fff",
    border: "1px solid #334155",
    borderRadius: 8,
    fontSize: 11,
    fontWeight: 900,
    cursor: "pointer",
  },
  navBtnActive: {
    padding: "5px 10px",
    background: "#0ea5e9",
    color: "#fff",
    border: "1px solid #38bdf8",
    borderRadius: 8,
    fontSize: 11,
    fontWeight: 900,
  },
  pageMobile: {
    gridTemplateColumns: "1fr",
    gridTemplateRows: "auto auto",
    overflowY: "auto",
    height: "auto",
    minHeight: "100%",
    padding: 8,
  },
  panelMobile: {
    minHeight: 260,
  },
  statGridMobile: {
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
  chartGridMobile: {
    gridTemplateColumns: "1fr",
    gridTemplateRows: "none",
    overflow: "visible",
  },
};